import { useEffect, useRef } from 'react'

import { useLocalParticipant } from '@livekit/components-react'

interface UseLocalAudioRecordingOptions {
  onAudioChunk: (blob: Blob) => Promise<void>
  timeslice?: number // milliseconds between chunks
}

export function useLocalAudioRecording(options: UseLocalAudioRecordingOptions) {
  const { localParticipant } = useLocalParticipant()
  const recorderRef = useRef<MediaRecorder | null>(null)

  useEffect(() => {
    // Get the local participant's audio track
    const audioTrackPublication = Array.from(localParticipant.audioTrackPublications.values())[0]
    const audioTrack = audioTrackPublication?.track

    if (!audioTrack || !audioTrack.mediaStreamTrack) {
      return
    }

    // Create MediaStream from the audio track
    const mediaStream = new MediaStream([audioTrack.mediaStreamTrack])

    // Determine the best supported mime type
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm'

    try {
      // Create and configure MediaRecorder
      const recorder = new MediaRecorder(mediaStream, { mimeType })

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          // Handle async function properly without making the event handler async
          options.onAudioChunk(event.data).catch(() => {
            // Silently handle errors to avoid breaking the recording
          })
        }
      }

      recorder.onerror = () => {
        // Silently handle MediaRecorder errors
      }

      // Start recording with timeslice (default 100)
      recorder.start(options.timeslice || 100)
      recorderRef.current = recorder
    } catch (error) {
      // Silently handle MediaRecorder creation errors
    }

    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop()
      }
      recorderRef.current = null
    }
  }, [
    localParticipant,
    localParticipant.audioTrackPublications.size,
    options.onAudioChunk,
    options.timeslice,
  ])

  return {
    isRecording: recorderRef.current?.state === 'recording',
    participantIdentity: localParticipant.identity,
  }
}
